import { Button, Checkbox, Col, DatePicker, DownOutlined, Dropdown, message, Row, Tabs } from 'gokwik-ui-kit'
import { useEffect, useReducer, useState } from 'react'
import DataTable from './Table'
import { PropertySelector } from './PropertySelector'
// import FilterComponent from './FilterComponent'
import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { handleError } from '@library/utilities/helpers/handleError'
import dayjs from 'dayjs'
import FilterComponent from './FilterComponent'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'
import { getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'

const initialState = {
    page: 1,
    limit: 15,
    property_id: '',
    source: 'ga_marketingmix',
    startDate: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    sortKey: '', //column key_name
    sortOrder: '', // 'desc' or 'asc',
}

const SessionAnalyticDetails = () => {
    const userData = useSelector(getUserData)
    const defaultEndDate = dayjs()
    const defaultStartDate = dayjs().subtract(2, 'day')
    const [openDropDown, setOpenDropDown] = useState(false)
    const [propertyData, setPropertyData] = useState(null)
    const [parameters, setParameters] = useState(initialState)
    const [filtersData, setFiltersData] = useState([])
    const [tableData, setTableData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [selectedFilterList, setSelectedFilterList] = useState([])
    const [columnHeaders, setColumnHeaders] = useState([])
    const [activeTabKey, setActiveTabKey] = useState(1)

    useEffect(() => {
        logEvent(
            'ga_session_analytic_details_landed',
            'visit',
            'exec_summary',
            userData?.userDetails?.email,
            userData?.merchantDetails?.m_id,
            userData?.merchantDetails?.short_name,
            userData?.userDetails?.name,
        )
        getAccount()
    }, [])

    useEffect(() => {
        if (parameters?.property_id) {
            getAllGASegmentData()
        }
    }, [parameters])

    const handleDateChange = (dates) => {
        if (dates) {
            setParameters((prevParams) => ({
                ...prevParams,
                page: 1,
                startDate: dayjs(dates[0]).format('YYYY-MM-DD'),
                endDate: dayjs(dates[1]).format('YYYY-MM-DD'),
            }))
        }
    }

    const rangePresets = [
        { label: 'Yesterday', value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
        { label: 'Today', value: [dayjs(), dayjs()] },
        { label: 'Last 3 Days', value: [dayjs().subtract(2, 'day'), dayjs()] },
        { label: 'Last 7 Days', value: [dayjs().subtract(6, 'day'), dayjs()] },
        { label: 'Last 30 Days', value: [dayjs().subtract(29, 'day'), dayjs()] },
    ]

    const ColumnSelectorDropDown = () => {
        const handleCheckboxClick = (keyName) => {
            setColumnHeaders((prevHeaders) =>
                prevHeaders.map((header) =>
                    header.key_name === keyName ? { ...header, is_default: !header.is_default } : header,
                ),
            )
        }

        const menuItems = columnHeaders?.map((item, index) => ({
            key: item?.key_name,
            label: (
                <Checkbox
                    key={item?.key_name}
                    onClick={() => handleCheckboxClick(item?.key_name)}
                    checked={item?.is_default}
                >
                    {item?.label}
                </Checkbox>
            ),
        }))

        const handleMenuClick = (info) => {}
        const handleOpenChange = (nextOpen, info) => {
            if (info?.source === 'trigger' || nextOpen) {
                setOpenDropDown(nextOpen)
            }
        }

        return (
            <div>
                <Dropdown
                    className='w-full'
                    menu={{
                        items: [
                            {
                                key: 'scroll-container',
                                label: (
                                    <div className='max-h-60 overflow-y-auto px-2'>
                                        {menuItems.map((menuItem, i) => (
                                            <div key={i} className='py-1'>
                                                {menuItem.label}
                                            </div>
                                        ))}
                                    </div>
                                ),
                            },
                        ],
                        onClick: (info) => handleMenuClick(info),
                    }}
                    onOpenChange={handleOpenChange}
                    open={openDropDown}
                >
                    <Button className='w-full' variant='default'>
                        Edit Columns
                    </Button>
                </Dropdown>
            </div>
        )
    }

    const tabItems = [
        {
            children: (
                <DataTable
                    id={1}
                    tableData={tableData}
                    loading={loading}
                    parameters={parameters}
                    setParameters={setParameters}
                    columnHeaders={columnHeaders}
                />
            ),
            key: 1,
            label: `Marketing Mix`,
            value: 'mmm',
        },
        {
            children: (
                <DataTable
                    id={2}
                    tableData={tableData}
                    loading={loading}
                    parameters={parameters}
                    setParameters={setParameters}
                    columnHeaders={columnHeaders}
                />
            ),
            label: `Product Mix`,
            key: 2,
        },
        {
            children: (
                <DataTable
                    id={3}
                    tableData={tableData}
                    loading={loading}
                    parameters={parameters}
                    setParameters={setParameters}
                    columnHeaders={columnHeaders}
                />
            ),
            label: `Landing Mix`,
            key: 3,
        },
    ]

    const getAccount = async () => {
        try {
            let response = await makeAPICall({
                method: 'get',
                url: process.env.REACT_APP_BASE_URL + APIEndPoints.getAccount,
            })
            if (
                response?.data?.status_code === 200 &&
                response?.data?.data?.is_connected &&
                response?.data?.data?.account_id
            ) {
                getPropertyData(response?.data?.data?.account_id)
            }
        } catch (error) {
            handleError(error)
            message.error('Error adding account data.')
        }
    }

    const getPropertyData = async (account_id) => {
        const response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_BASE_URL + `/api/dashboard/ga-property-list?account_id=${account_id}`,
        })

        if (response?.status === 200) {
            setPropertyData(response?.data?.data)

            let propertyId =
                response?.data?.data?.propertyList?.length > 0
                    ? response?.data?.data?.propertyList[0]?.property_id
                    : null

            if (propertyId) {
                setParameters((prevParams) => {
                    const newParams = {
                        ...prevParams,
                        property_id: propertyId,
                    }

                    return newParams
                })
            }
        }
    }

    const getAllFilters = async () => {
        const response = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_BASE_URL + '/api/dashboard/ga-segmentation-filter',
            params: parameters,
            skipLoader: true,
        })

        setFiltersData(response?.data?.data?.results)

        // setTableData((prevData) => ({
        //     ...prevData,
        //     filters: response?.data?.data?.results,
        // }))
    }

    const getAllColumns = async () => {
        try {
            let response = await makeAPICall({
                method: 'get',
                url: process.env.REACT_APP_BASE_URL + APIEndPoints?.getColumnPreferences,
                params: {
                    page: parameters?.source,
                    dashboard: 'merchant',
                },
            })
            if (response?.data?.status_code === 200) {
                setColumnHeaders(response?.data?.data)
            }
        } catch (error) {
            handleError(error)
            message.error('Error fetching column preferences.')
        }
    }

    const getAllGASegmentData = async (selectedFiltersArray?, cta_name?) => {
        // '?page=2&limit=15&startDate=2025-02-18&endDate=2025-03-25&property_id=453434626&source=ga_marketingmix'
        if (parameters?.page > 1 && cta_name === 'apply_filters_cta') {
            setParameters((prevParams) => {
                const newParams = {
                    ...prevParams,
                    page: 1,
                }

                return newParams
            })
        }
        const tempParams = { ...parameters }

        setLoading(true)
        const response = await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_BASE_URL + '/api/dashboard/ga-segmentation',
            params: tempParams,
            payload: { filters: selectedFiltersArray ? selectedFiltersArray : selectedFilterList },
            skipLoader: true,
        })

        if (response?.status === 200) {
            if (response?.data?.data) {
                setTableData(response?.data?.data)

                if (parameters?.page === 1) {
                    if (selectedFiltersArray === undefined || selectedFilterList?.length === 0) {
                        getAllFilters()
                    }
                    getAllColumns()
                }
            }
        }

        setLoading(false)
    }

    return (
        <div className='w-full'>
            <Row align={'middle'}>
                <Col span={18}>
                    <Row align={'middle'}>
                        <Col
                        // span={6}
                        >
                            <div className='text-2xl font-semibold'>Session Analytics</div>
                        </Col>

                        {/* <Col span={18}>
                            <div className='text-[#000000E0] text-xs font-normal bg-[#00000005] p-2 rounded-md w-fit'>
                                GA Data will not exactly match with GoKwik numbers. For accurate data Upgrade to Kwik
                                Pass.
                            </div>
                        </Col> */}
                    </Row>
                </Col>

                <Col span={6}>
                    {propertyData && (
                        <div>
                            <PropertySelector
                                data={propertyData}
                                parameters={parameters}
                                setParameters={setParameters}
                            />
                        </div>
                    )}
                </Col>
            </Row>

            <br />
            <br />
            <br />

            <div>
                {/* <div>Conversion Rate</div> */}

                <br />

                <div>
                    <header className='text-base font-semibold'>Deeper Analysis</header>
                    <br />
                    <div>
                        <Tabs
                            tabBarExtraContent={
                                <div className='flex gap-1'>
                                    <DatePicker.RangePicker
                                        placeholder={['Select date', 'Select date']}
                                        className='border-solid border-gray-600'
                                        onChange={handleDateChange}
                                        defaultValue={[defaultStartDate, defaultEndDate]}
                                        presets={rangePresets as any}
                                    />

                                    {filtersData && (
                                        <FilterComponent
                                            parameters={parameters}
                                            setParameters={setParameters}
                                            filtersData={filtersData}
                                            setFiltersData={setFiltersData}
                                            getAllGASegmentData={getAllGASegmentData}
                                            setSelectedFilterList={setSelectedFilterList}
                                            activeTabKey={activeTabKey}
                                        />
                                    )}

                                    {columnHeaders && <ColumnSelectorDropDown />}
                                </div>
                            }
                            onChange={(key: any) => {
                                setActiveTabKey(key)
                                let source
                                if (key === 1 || key === '1') {
                                    source = 'ga_marketingmix'
                                } else if (key === 2 || key === '2') {
                                    source = 'ga_productmix'
                                } else if (key === 3 || key === '3') {
                                    source = 'ga_landingpage'
                                }

                                setSelectedFilterList([])
                                setParameters((prevParams) => {
                                    const newParams = {
                                        ...prevParams,
                                        page: 1,
                                        source,
                                    }

                                    return newParams
                                })
                            }}
                            defaultActiveKey='1'
                            type='card'
                            size={'middle'}
                            style={{ marginBottom: 32 }}
                            //@ts-ignore
                            items={tabItems}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SessionAnalyticDetails
